import {
  FeedType,
  FeedTypeMap,
  Market,
  Match,
  MatchPeriod,
  SportEvent,
  SportEventsMap,
} from "@/types/SportsApiResponses";

export const getMatchFeedType = (match: Pick<Match, "status">): FeedType => {
  return match.status === "live" ? "live" : "line";
};

export const getScoresOrder = (match: Match): number[] => {
  return match.participants.map((p) => p.id);
};

export const getSportEventsFeedTypeMap = (eventsMap: SportEventsMap) => {
  const eventsFeedTypeMap: FeedTypeMap = {};

  for (const eventId in eventsMap) {
    const event = eventsMap[eventId];
    eventsFeedTypeMap[event.id] = getMatchFeedType(event.match);
  }

  return eventsFeedTypeMap;
};

const sportsWithNoPeriodScores = [
  1, // Football
];

const sportsWithNoPeriodScoresSet = new Set(sportsWithNoPeriodScores);

export const shouldShowPeriodsScores = (sportEvent: SportEvent) => {
  return !sportsWithNoPeriodScoresSet.has(sportEvent.sportId);
};

/**
 * The participant leading a period's score — used to tint the period winner in
 * score strips. Prefers the API's own `winner` when present; otherwise derives
 * it from the period scores. A tie (or missing/short scores) has no winner.
 * Pure display derivation: same input → same output.
 */
export const getPeriodWinner = (
  period: Pick<MatchPeriod, "winner" | "scores">
): number | undefined => {
  if (period.winner != null) return period.winner;

  const entries = Object.entries(period.scores ?? {});
  if (entries.length < 2) return undefined;

  let leader: number | undefined;
  let best = -Infinity;
  let tied = false;
  for (const [participant, value] of entries) {
    const score = Number(value);
    if (!Number.isFinite(score)) return undefined;
    if (score > best) {
      best = score;
      leader = Number(participant);
      tied = false;
    } else if (score === best) {
      tied = true;
    }
  }
  return tied ? undefined : leader;
};

export const validateFeedType = (value?: string | null): FeedType => {
  return value === "live" ? "live" : "line";
};

export const getRefetchIntervalByFeedType = (feedType: FeedType) => {
  return feedType === "live" ? 5000 : 60000;
};